import { useTranslation } from "react-i18next";
import type {
  ReportMessageStatusBucketName,
  ReportMessageSummaryResponse,
} from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";
import {
  OUTGOING_REPORT_METRIC_CARDS,
  outgoingReportMetricToneClass,
} from "./outgoing-report-presentation.js";

export function OutgoingReportSummary({
  summary,
  statusBucket,
  onSelectStatusBucket,
}: {
  summary: ReportMessageSummaryResponse;
  statusBucket: ReportMessageStatusBucketName | undefined;
  onSelectStatusBucket: (bucket: ReportMessageStatusBucketName | null) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  return (
    <div
      className="grid shrink-0 grid-cols-2 gap-1.5 sm:grid-cols-3 xl:grid-cols-6"
      data-testid="reports-outgoing-summary"
      role="toolbar"
      aria-label={t("reports.outgoing.metricsLabel")}
    >
      {OUTGOING_REPORT_METRIC_CARDS.map((card) => {
        const selected =
          card.statusBucket == null
            ? statusBucket == null
            : statusBucket === card.statusBucket;
        const Icon = card.icon;
        const label = t(card.labelKey);
        const count = new Intl.NumberFormat(i18n.language).format(summary[card.key]);
        return (
          <button
            key={card.key}
            type="button"
            aria-pressed={selected}
            aria-label={`${label}: ${count}`}
            onClick={() => onSelectStatusBucket(card.statusBucket)}
            className={cn(
              "grid gap-0.5 rounded-md border bg-surface px-2 py-1.5 text-start transition-colors",
              "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50",
              selected
                ? "border-accent/45 bg-accent/8 ring-1 ring-accent/25"
                : "border-line/70 hover:bg-surface-muted",
            )}
            data-testid={`reports-outgoing-metric-${card.key}`}
          >
            <span className="flex items-center gap-1 text-[0.65rem] font-medium text-muted">
              <span
                className={cn(
                  "inline-grid size-5 shrink-0 place-items-center rounded-full",
                  outgoingReportMetricToneClass(card.tone),
                )}
                aria-hidden="true"
              >
                <Icon size={11} />
              </span>
              {label}
            </span>
            <span className="text-[1rem] font-semibold tabular-nums tracking-[-0.02em]">
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
